import * as React from 'react';
import { Component } from 'react';
import _ from 'lodash';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import i18n from 'i18n';
import { Link } from 'react-router-dom';
import MockServiceIcon from 'components/icons/MockServiceIcon';
import { ButtonProps, Tab, Tabs } from 'react-bootstrap';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import Reporting from 'views/Analytics/AnalyticsMockService/Reporting/Reporting';
import { refreshMsMetadataThunk, selectMsAnalyticsContentTab } from 'store/analytics/ui/analyticsUiActions';
import InspectionView from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewMar';
import { selectDataContextThunk } from 'store/analytics/data/analyticsDataActions';
import CopyToClipboardButton from 'components/buttons/CopyToClipboardButton';
import MixpanelService from 'utils/mixpanelService';
import { GO_TO_MOCK_SERVICE_FROM_ANALYTICS } from 'utils/mixpanelEvents';
import TransactionInspectionView from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewTransactional';
import StatusIndicator from 'components/statusIndicator/StatusIndicator';
import { showBackdrop } from 'store/ui/backdrop/backdropActions';
import WebhookView from 'views/Analytics/AnalyticsMockService/Webhook/WebhookView';
import HttpCallsView from './HttpCalls/HttpCallsView';
import StateChangesView from 'views/Analytics/AnalyticsMockService/StateChanges/StateChangesView';
import EndpointValue from '../../../components/endPoint/EndpointValue';

type AnalyticsMockServiceProps = { mockService: MockServiceAnalytics; variant?: ButtonProps['variant'] };
type AllProps = StateProps & AnalyticsMockServiceProps & DispatchProps;

/**
 * Wrapper for showing analytics of a virtual service.
 */
class AnalyticsMockService extends Component<AllProps> {
  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
    this.pollUpdateOfMockServiceMeta();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  /**
   * Starts polling for update of the virtual service meta. Polling happens only if
   * component is mounted on the screen. Updates it every 60 seconds.
   */
  pollUpdateOfMockServiceMeta = async () => {
    while (this._isMounted) {
      if (!_.isNil(this.props.mockService)) {
        this.props.refreshMsMetadataThunk(this.props.mockService.id);
      }
      await new Promise(resolve => setTimeout(resolve, 60000));
    }
  };

  render() {
    const { activeUiContext, mockService } = this.props;
    const activeTabKey = activeUiContext ? activeUiContext.activeTab : 'inspection';
    const helpId = mockService.type === 'SV' ? 'ms-analytics-help' : 'analytics-transaction-help';

    return (
      <>
        {/* Header */}
        <div className="mb-2" style={{ minHeight: 30 }}>
          <div className={'float-right font-size-lg'}>
            {mockService && mockService.deleted ? (
              <span className={'text-muted cursor-pointer'}> {i18n.t('analytics:mockService.goto')}</span>
            ) : (
              <Link
                tabIndex={0}
                onClick={async () => {
                  this.props.showBackdrop('Opening Virtual Service...');
                  MixpanelService.track(GO_TO_MOCK_SERVICE_FROM_ANALYTICS(mockService.id), true);
                }}
                to={`/mock-services/detail/${mockService.id}`}
                className={'align-middle qa-redirect-to-ms'}
              >
                {i18n.t('analytics:mockService.goto')}
              </Link>
            )}
          </div>
          <h1 className={'d-inline-block align-middle mb-0 mr-3 text-truncate'} style={{ maxWidth: 450 }}>
            <MockServiceIcon size={24} className={'mr-3'} type={mockService.type} />
            <span title={mockService.name} style={{ position: 'relative', bottom: '1px' }}>
              {mockService.name}
            </span>
          </h1>

          {/* Status */}
          {mockService.meta && (
            <div className="d-inline-block mr-5">
              <StatusIndicator variant={mockService.meta.status as any} />
            </div>
          )}
          {/* ./Status */}

          <div
            className="d-inline-block align-middle position-relative text-truncate"
            style={{ top: 2, maxWidth: '100%' }}
          >
            {/* Location name*/}
            <strong className={'mr-2'}>Location:</strong> {mockService.meta ? mockService.meta.locationName : ''}
            {/* Endpoint  */}
            <strong className={'ml-4 mr-2'}>Endpoint:</strong>{' '}
            <EndpointValue endpoints={mockService.meta ? mockService.meta.endpoints : []} />
            {/* ./Endpoint  */}
          </div>
        </div>

        {/* Body */}
        <div className="position-relative">
          <HelpTooltip
            id={helpId}
            className="ml-3 float-right"
            style={{ position: 'absolute', right: '0px', top: '3px' }}
            fontSize={22}
          />

          <Tabs
            defaultActiveKey={activeTabKey}
            activeKey={activeTabKey}
            id="mock-service-analytics-tabs"
            onSelect={(tabKey: any) => this.props.selectMsAnalyticsContentTab(tabKey)}
          >
            <Tab eventKey="inspection" title="Inspection" mountOnEnter={true} unmountOnExit={true}>
              {mockService.type === 'TRANSACTIONAL' && <TransactionInspectionView mockService={mockService} />}
              {mockService.type === 'SV' && (
                <InspectionView serviceImageFilePath={_.get(mockService, 'meta.metadata.serviceImageFilePath')} />
              )}
            </Tab>
            <Tab eventKey="reporting" title="Reporting" mountOnEnter={true} unmountOnExit={true}>
              <Reporting selectedMockService={mockService} />
            </Tab>
            <Tab eventKey="webhook" title="Webhooks" mountOnEnter={true} unmountOnExit={true}>
              <WebhookView mockService={mockService} />
            </Tab>
            <Tab eventKey="httpCall" title="HTTP Calls" mountOnEnter={true} unmountOnExit={true}>
              <HttpCallsView mockService={mockService} />
            </Tab>
            <Tab eventKey="stateChanges" title="State Changes" mountOnEnter={true} unmountOnExit={true}>
              <StateChangesView mockService={mockService} />
            </Tab>
          </Tabs>
        </div>
      </>
    );
  }
}

const mapStateToProps = ({ analytics, mockservices }: ApplicationState) => ({
  bzmLocations: mockservices.locData,
  activeUiContext: analytics.ui.activeUiContext,
  data: analytics.data,
});

const mapDispatchToProps = {
  selectMsAnalyticsContentTab,
  selectDataContextThunk,
  refreshMsMetadataThunk,
  showBackdrop,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(AnalyticsMockService);
